import {
  downloadGeodataFile,
  getFromBase64Csib,
  getFromDc,
  GetFromDcResponse,
} from './generated';
import { Client } from './generated/client/types.gen';
import { useQuery, UseQueryResult } from '@tanstack/solid-query';
import { returnMappedOrThrow } from '@trimble/utilities';
import {
  CalibrationFileInfo,
  CoordinateSystemFromDcFileResponse,
} from './coordinates-api-service.types';
import { mapCoordinateSystemData } from './coordinates-api-service.mappers';

export class CoordinatesApiService {
  constructor(private readonly client: Client) {}

  public getCoordinateDataFromDc(
    calibrationFileInfo: CalibrationFileInfo | undefined,
    initialData?: GetFromDcResponse
  ): UseQueryResult<GetFromDcResponse, Error> {
    return useQuery(() => ({
      initialData: initialData
        ? mapCoordinateSystemData(initialData)
        : undefined,
      queryKey: ['getCoordinateDataFromDc', calibrationFileInfo?.fileName],
      queryFn: async () => {
        let calibrationFileContents: string;
        try {
          calibrationFileContents = await (
            await fetch(calibrationFileInfo!.fileDownloadLink)
          ).text();
        } catch {
          throw new Error(
            `Error fetching calibration file: ${calibrationFileInfo!.fileName}.`
          );
        }

        const response = await getFromDc({
          body: calibrationFileContents,
          headers: {
            //eslint-disable-next-line @typescript-eslint/naming-convention
            'Content-Type': 'text/plain',
          },
          client: this.client,
        });

        return returnMappedOrThrow(
          response,
          'Error occured getting Coordinates data from calibration file',
          mapCoordinateSystemData
        );
      },
      refetchOnWindowFocus: false,
      enabled: !!calibrationFileInfo,
    }));
  }

  public fetchGeoDataFiles = async (
    coordinatesDataValue: CoordinateSystemFromDcFileResponse | undefined
  ): Promise<Map<string, ArrayBuffer>> => {
    return downloadGeoDataFilesFromResponse(this.client, coordinatesDataValue);
  };
}

export async function downloadGeoDataFilesFromResponse(
  client: Client,
  coordinatesData: CoordinateSystemFromDcFileResponse | undefined
): Promise<Map<string, ArrayBuffer>> {
  if (!coordinatesData?.coordinateSystem?.files) {
    return new Map<string, ArrayBuffer>();
  }

  const filePromises = Object.values(coordinatesData.coordinateSystem.files)
    .filter((name) => !name.endsWith('.ggf'))
    .map(async (fileName) => {
      const response = await downloadGeodataFile({
        query: { filename: fileName },
        client,
        parseAs: 'blob',
      });

      if (response.error) {
        throw new Error(`Failed to fetch geodata file: ${fileName}`);
      }

      return {
        name: fileName,
        buffer: await (response.data as Blob).arrayBuffer(),
      };
    });

  const namedBuffers = await Promise.all(filePromises);
  const geoDataFileMap = new Map<string, ArrayBuffer>();
  for (const { name, buffer } of namedBuffers) {
    geoDataFileMap.set(name, buffer);
  }
  return geoDataFileMap;
}

export async function fetchGeoDataFilesByCsib(
  client: Client,
  csib64: string
): Promise<Map<string, ArrayBuffer>> {
  const response = await getFromBase64Csib({
    //eslint-disable-next-line @typescript-eslint/naming-convention
    query: { csib_64: csib64 },
    client,
  });

  if (response.error) {
    throw new Error(
      `Failed to fetch coordinate system from CSIB: ${JSON.stringify(response.error)}`
    );
  }

  const coordinatesData = response.data as
    | CoordinateSystemFromDcFileResponse
    | undefined;

  return downloadGeoDataFilesFromResponse(client, coordinatesData);
}
